import React, { useEffect, useState } from "react"
import Select from "react-select"
import AsyncSelect from "react-select/async"
import { get } from "helpers/api_helper"
import { withFloatingSurface } from "components/Common/selectSurface"
import { noOrganizer, provisionalVenue } from "./eventCreationForm"

const accountOption = (account) => {
  const info = account.organizerInfo || account
  return {
    label:
      info.clientType === "company"
        ? info.companyName
        : [
            info.firstName || account.firstName,
            info.lastName || account.lastName,
          ]
            .filter(Boolean)
            .join(" "),
    value: {
      id: account.id || account._id,
      firstName: info.firstName || account.firstName,
      lastName: info.lastName || account.lastName,
      clientType: info.clientType,
      companyName: info.companyName,
      email: info.email || account.email,
      phone: info.phone || account.phone || account.contactInfo?.phone || "",
    },
  }
}
const noOrganizerOption = { label: "No organizer", value: noOrganizer }
const loadClubOrganizers = async (clubId) => {
  const accounts = []
  let page = 1,
    totalPages = 1
  do {
    const response = await get(
      "/clubs/" + clubId + "/organizers?status=activated&limit=100&page=" + page
    )
    if (!Array.isArray(response?.data?.results))
      throw new Error("Invalid response")
    accounts.push(...response.data.results)
    totalPages = response.data.totalPages || 1
    page += 1
  } while (page <= totalPages)
  return accounts.filter((account) => account.status === "activated")
}
export default function CreateEventChoices({ form, locked, onReadiness }) {
  const { values, setFieldValue, setFieldTouched, touched, errors } = form
  const [clubError, setClubError] = useState("")
  const [organizerError, setOrganizerError] = useState("")
  const [organizers, setOrganizers] = useState([])
  const [loading, setLoading] = useState(false)
  const [retry, setRetry] = useState(0)
  const clubId = values.club?.id
  useEffect(() => {
    let current = true
    setOrganizers([])
    setOrganizerError("")
    if (!clubId) {
      setLoading(false)
      onReadiness(true)
      return () => {
        current = false
      }
    }
    setLoading(true)
    onReadiness(false)
    loadClubOrganizers(clubId)
      .then((accounts) => {
        if (!current) return
        const options = accounts.map(accountOption)
        setOrganizers(options.length ? options : [noOrganizerOption])
        if (!options.length) setFieldValue("organizer", noOrganizer)
        onReadiness(true)
      })
      .catch(() => {
        if (current) {
          setOrganizerError("Could not load this club's organizers.")
          onReadiness(false)
        }
      })
      .finally(() => {
        if (current) setLoading(false)
      })
    return () => {
      current = false
    }
  }, [clubId, retry, onReadiness, setFieldValue])
  const loadClubs = async (input) => {
    setClubError("")
    if (input.trim().length === 1) return []
    try {
      const response = await get(
        "/clubs?" +
          new URLSearchParams({
            status: "activated",
            search: input.trim(),
            page: 1,
            limit: 25,
          })
      )
      if (!Array.isArray(response?.data?.results))
        throw new Error("Invalid response")
      return [
        { label: "TBA (To be announced)", value: { clubName: "TBA" } },
        { label: "No Club", value: { clubName: null } },
        ...response.data.results.map((club) => ({
          label:
            club.clubName +
            (club.location?.city ? " · " + club.location.city : ""),
          value: club,
        })),
      ]
    } catch (_error) {
      setClubError("Could not load clubs. Type again to retry.")
      return []
    }
  }
  const loadOrganizers = async (input) => {
    setOrganizerError("")
    if (input.trim().length === 1) return [noOrganizerOption]
    try {
      const response = await get(
        "/admin/organizers?" +
          new URLSearchParams({
            status: "activated",
            search: input.trim(),
            page: 1,
            limit: 25,
          })
      )
      if (!Array.isArray(response?.data?.results))
        throw new Error("Invalid response")
      return [noOrganizerOption, ...response.data.results.map(accountOption)]
    } catch (_error) {
      setOrganizerError("Could not load organizers. Type again to retry.")
      return []
    }
  }
  const selectOrganizer = (option) => {
    const organizer = option?.value || null
    setFieldValue("organizer", organizer)
    setFieldValue(
      "directorName",
      organizer?.id
        ? [organizer.firstName, organizer.lastName].filter(Boolean).join(" ")
        : ""
    )
    setFieldValue("email", organizer?.email || "")
    setFieldValue("phone", organizer?.phone || "")
  }
  const common = {
    isDisabled: locked,
    classNamePrefix: "creation-select",
    noOptionsMessage: () => "Type at least 2 characters to search",
    styles: withFloatingSurface({ control: (base) => ({ ...base, minHeight: 44 }) }),
  }
  const selectedOrganizer = values.organizer
    ? {
        value: values.organizer,
        label:
          values.organizer.companyName ||
          [values.organizer.firstName, values.organizer.lastName]
            .filter(Boolean)
            .join(" "),
      }
    : null
  return (
    <div className="creation-grid">
      <div>
        <label htmlFor="create-club">
          Club <span aria-hidden="true">*</span>
        </label>
        <AsyncSelect
          {...common}
          inputId="create-club"
          name="club"
          defaultOptions
          loadOptions={loadClubs}
          value={
            values.club
              ? {
                  value: values.club,
                  label:
                    values.club.clubName === null
                      ? "No Club"
                      : values.club.clubName,
                }
              : null
          }
          onBlur={() => setFieldTouched("club", true)}
          aria-invalid={Boolean(touched.club && errors.club)}
          aria-describedby="create-club-error"
          placeholder="Search clubs, or choose TBA / No Club"
          onChange={(option) => {
            setFieldValue("club", option?.value || null)
            setFieldValue("country", "")
            setFieldValue("city", "")
            selectOrganizer(null)
            onReadiness(!option?.value?.id)
          }}
        />
        <small id="create-club-error" className="text-danger">
          {clubError || (touched.club && errors.club)}
        </small>
      </div>
      <div>
        <label htmlFor="create-organizer">
          Organizer <span aria-hidden="true">*</span>
        </label>
        {provisionalVenue(values.club) ? (
          <AsyncSelect
            {...common}
            key={"global-" + retry}
            inputId="create-organizer"
            name="organizer"
            defaultOptions
            loadOptions={loadOrganizers}
            value={selectedOrganizer}
            onChange={selectOrganizer}
            onBlur={() => setFieldTouched("organizer", true)}
            aria-describedby="create-organizer-error"
          />
        ) : (
          <Select
            {...common}
            inputId="create-organizer"
            name="organizer"
            options={organizers}
            value={selectedOrganizer}
            isDisabled={locked || !clubId || loading || Boolean(organizerError)}
            isLoading={loading}
            placeholder={loading ? "Loading organizers…" : "Select organizer"}
            onChange={selectOrganizer}
            onBlur={() => setFieldTouched("organizer", true)}
            aria-describedby="create-organizer-error"
          />
        )}
        <small id="create-organizer-error" className="text-danger">
          {organizerError || (touched.organizer && errors.organizer)}
        </small>
        {organizerError && clubId && (
          <button
            type="button"
            className="btn btn-link btn-sm"
            onClick={() => setRetry((value) => value + 1)}
          >
            Retry organizers
          </button>
        )}
      </div>
    </div>
  )
}
